import React, { type JSX } from "react";
import ConversationStarters from "../../ConversationStarters_Component";
import BotMessageBody from "./ChatMessageItem";
import { ReconnectionNotice, UserMessage, WelcomeHeader } from "./ChatMessageParts";
import { isInternalMessage } from "./chatbotConstants";
import type { ChatFeedback } from "../../hooks/useChatFeedback";

export interface ChatMessageListProps {
  chatMessages: any[];
  allFacts: any[];
  allDocuments: any[];
  onDocumentClick: (document: any) => void;
  feedback: ChatFeedback;
  selectedUseCase: string | null;
  showConversationStarters: boolean;
  inWorkspaceMode: boolean;
  pillsDisabled: boolean;
  onSelectStarter: (followUpPrompt: string, useCaseId: string) => void;
}

/** Use cases whose welcome header disappears with the conversation starters. */
const SEARCH_USE_CASES = new Set(["past-deals", "research-desk"]);

/** The message itself: user bubble, welcome header, or a full-width bot body. */
function messageBody(
  message: any,
  index: number,
  props: ChatMessageListProps
): JSX.Element | null {
  const { feedback } = props;

  if (message.sender === "user") {
    return <UserMessage text={message.text} />;
  }

  // Special styling for the first bot message (welcome message)
  const isWelcomeMessage = index === 0;

  // Welcome message: centered header styling - only show while starters are visible,
  // never in workspace mode
  if (isWelcomeMessage && props.showConversationStarters && !props.inWorkspaceMode) {
    return <WelcomeHeader />;
  }

  // Hide welcome message once conversation starters are gone (search use cases only)
  if (isWelcomeMessage && SEARCH_USE_CASES.has(props.selectedUseCase || "")) {
    return null;
  }

  return (
    <div className="w-full">
      <BotMessageBody
        message={message}
        index={index}
        allFacts={props.allFacts}
        allDocuments={props.allDocuments}
        onDocumentClick={props.onDocumentClick}
        isLatestBot={index === feedback.latestBotMessageIndex}
        feedbackChoice={feedback.messageFeedback[index]}
        isScaling={feedback.scalingMessages.has(index)}
        onFeedback={feedback.handleFeedback}
      />
    </div>
  );
}

/** One row of the message list: the message itself plus any trailing starters. */
function MessageRow({
  message,
  index,
  props,
}: {
  message: any;
  index: number;
  props: ChatMessageListProps;
}): JSX.Element {
  const isUser = message.sender === "user";

  return (
    <>
      <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        {messageBody(message, index, props)}
      </div>

      {/* Show conversation starters after first message (index 0) */}
      {index === 0 && props.showConversationStarters && (
        <ConversationStarters onSelectStarter={props.onSelectStarter} disabled={props.pillsDisabled} />
      )}
    </>
  );
}

/** The rendered conversation, skipping internal control messages. */
export default function ChatMessageList(props: ChatMessageListProps): JSX.Element {
  return (
    <>
      {props.chatMessages.map((message, index) => {
        // Skip internal system messages (CONTROL_*, SYSTEM_*)
        if (isInternalMessage(message)) {
          return null;
        }

        // Render reconnection status messages as inline status bars
        if (message.type?.startsWith("reconnection-")) {
          return <ReconnectionNotice key={index} message={message} />;
        }

        return <MessageRow key={index} message={message} index={index} props={props} />;
      })}
    </>
  );
}
